/**
 * WebsiteAiBooking — customer-facing AI Booking Assistant (Phase 2, public).
 *
 * A website visitor describes their trip in plain English; the SAME channel-
 * independent AI engine (parse_booking, channel="website") extracts a structured
 * DRAFT and asks follow-up questions. A customer-friendly quote (distance, time,
 * estimated fare) is shown using the EXISTING read-only estimator. On confirm,
 * the request enters the dispatcher REVIEW QUEUE — nothing is booked or
 * dispatched automatically. The customer is told their request is "received and
 * being reviewed", never "booked".
 */
import React, { useState, useRef, useEffect } from "react";
import {
  Wand2, Send, Loader2, User, Phone, MapPin, Clock, CreditCard, Users,
  Accessibility, RotateCcw, CheckCircle2, AlertCircle, X, Route, Car,
  ChevronDown, ChevronRight, MessageSquare, ShieldCheck, Pencil, Check, Banknote,
} from "lucide-react";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { forwardGeocode } from "./PlaceAutocomplete";
import { useAuth } from "../contexts/AuthContext";

const VEHICLE_LABEL = { car: "Standard Saloon", bus: "Minibus" };
const gbp = (v) => `£${Number(v || 0).toFixed(2)}`;

const fareLines = (quote) => {
  const bd = quote.breakdown || {};
  const ex = bd.extras || {};
  const lines = [];
  lines.push([quote.is_fixed_route ? "Fixed route fare" : "Meter fare", gbp(bd.meter_total)]);
  if ((ex.extra_stops_total || 0) > 0) lines.push([`Extra stops${ex.extra_stops_count ? ` (${ex.extra_stops_count})` : ""}`, gbp(ex.extra_stops_total)]);
  if ((ex.dog_total || 0) > 0) lines.push(["Pet charge", gbp(ex.dog_total)]);
  if ((ex.terminal_total || 0) > 0) lines.push(["Terminal fee", gbp(ex.terminal_total)]);
  if ((bd.late_night_amount || 0) > 0) lines.push(["Late night uplift", gbp(bd.late_night_amount)]);
  return lines;
};

const newSessionId = () =>
  (window.crypto?.randomUUID?.() || `web-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const SCHEDULE_LABEL = (s) => {
  if (!s || s === "asap") return "As soon as possible";
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const DraftRow = ({ icon: Icon, label, value, missing }) => (
  <div className={`flex items-start gap-2 px-3 py-1.5 rounded-lg border ${missing ? "border-dashed border-[#F59E0B] bg-[#FFFBEB]" : "border-border bg-white"}`}>
    <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${missing ? "text-[#B45309]" : "text-muted-foreground"}`} />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
      <div className={`text-sm truncate ${missing ? "text-[#B45309] italic" : "font-medium"}`}>{missing ? "Needed" : (value || "—")}</div>
    </div>
  </div>
);

// Inline-editable draft row. Tap it (or the pencil) to type a value directly —
// so a booking never dead-ends when the AI couldn't infer a required field.
// Saving persists to the server draft via the patch endpoint (onSave).
const EditableDraftRow = ({ icon: Icon, label, value, missing, field, type = "text", placeholder, onSave, testId }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setVal(value || ""); }, [value, editing]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = async () => {
    const trimmed = (val || "").trim();
    if (!trimmed || trimmed === (value || "")) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(field, trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#002FA7] bg-white" data-testid={`${testId}-editing`}>
        <Icon className="w-3.5 h-3.5 flex-shrink-0 text-[#002FA7]" />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
          <input
            ref={inputRef}
            type={type}
            inputMode={type === "tel" ? "tel" : undefined}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") setEditing(false); }}
            placeholder={placeholder}
            className="w-full outline-none text-sm font-medium bg-transparent py-0.5"
            data-testid={`${testId}-input`}
          />
        </div>
        <button type="button" onClick={commit} disabled={saving} className="p-1 rounded-md text-[#16A34A] hover:bg-[#DCFCE7]" title="Save" data-testid={`${testId}-save`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </button>
        <button type="button" onClick={() => setEditing(false)} className="p-1 rounded-md text-muted-foreground hover:bg-secondary" title="Cancel" data-testid={`${testId}-cancel`}>
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`w-full text-left flex items-start gap-2 px-3 py-1.5 rounded-lg border transition-colors ${missing ? "border-dashed border-[#F59E0B] bg-[#FFFBEB] hover:border-[#B45309]" : "border-border bg-white hover:border-[#002FA7]"}`}
      data-testid={testId}
    >
      <Icon className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${missing ? "text-[#B45309]" : "text-muted-foreground"}`} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
        <div className={`text-sm truncate ${missing ? "text-[#B45309] italic" : "font-medium"}`}>{missing ? "Tap to add" : (value || "—")}</div>
      </div>
      <Pencil className="w-3 h-3 mt-1 flex-shrink-0 text-muted-foreground/70" />
    </button>
  );
};

const EXAMPLES = [
  "I need a taxi from Basingstoke Station to Heathrow tomorrow at 5am.",
  "Book a taxi to North Hampshire Hospital this afternoon.",
  "I need a return airport trip on Friday for 2 people.",
];

// Localised welcome copy for the operator's enabled languages. Place names are
// intentionally left untranslated. Falls back to English for any other code.
const WELCOME = {
  en: {
    intro: "Hi! Where are you travelling? Try:",
    placeholder: "Type your trip details…",
    examples: EXAMPLES,
  },
  sn: {
    intro: "Mhoro! Muri kuenda kupi? Edzai:",
    placeholder: "Nyorai kwamuri kuenda…",
    examples: [
      "Ndinoda tekisi kubva paBasingstoke Station kuenda kuHeathrow mangwana na5am.",
      "Ndinoda tekisi kuNorth Hampshire Hospital masikati ano.",
      "Ndinoda rwendo rwekudzokera kuairport neChishanu, vanhu 2.",
    ],
  },
  nd: {
    intro: "Sawubona! Uya ngaphi? Zama:",
    placeholder: "Bhala imininingwane yohambo lwakho…",
    examples: [
      "Ngifuna itekisi ukusuka eBasingstoke Station ngiya eHeathrow kusasa ngo-5am.",
      "Ngifuna itekisi ngiye eNorth Hampshire Hospital namuhla ntambama.",
      "Ngidinga uhambo lokubuya lwe-airport ngoLwesihlanu, abantu aba-2.",
    ],
  },
};

export const WebsiteAiBooking = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(newSessionId);
  const sessionIdRef = useRef(null);
  if (sessionIdRef.current === null) sessionIdRef.current = sessionId;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { reference, message }
  const [lang, setLang] = useState("en");
  const scrollRef = useRef(null);

  // Open the assistant in the operator's DEFAULT language (Region & Language
  // settings). Automatic per-message detection still applies once the customer
  // types. Falls back to English if unset/unavailable.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get("/public/branding")
      .then(({ data }) => {
        if (cancelled) return;
        const d = (data?.default_language || "en").toLowerCase();
        setLang(WELCOME[d] ? d : "en");
      })
      .catch(() => { if (!cancelled) setLang("en"); });
    return () => { cancelled = true; };
  }, [open]);

  const welcome = WELCOME[lang] || WELCOME.en;

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, open]);

  // Customer-friendly fare/ETA quote — geocodes the AI's pickup/dropoff text and
  // reuses the EXISTING read-only public estimator. Never creates a ride.
  useEffect(() => {
    const p = draft?.pickup_text, d = draft?.dropoff_text;
    if (!p || !d) { setQuote(null); setQuoteError(null); return; }
    let cancelled = false;
    setQuoteLoading(true); setQuoteError(null);
    (async () => {
      // Prefer coordinates the resolver already found (stored on the draft) and
      // DO NOT re-geocode the text when we already have valid coords. Only fall
      // back to browser geocoding for a field whose coords are missing.
      const hasPickupCoords = Number.isFinite(draft.pickup_lat) && Number.isFinite(draft.pickup_lng);
      const hasDropoffCoords = Number.isFinite(draft.dropoff_lat) && Number.isFinite(draft.dropoff_lng);
      const pg = hasPickupCoords
        ? { lat: draft.pickup_lat, lng: draft.pickup_lng }
        : await forwardGeocode(p);
      const dg = hasDropoffCoords
        ? { lat: draft.dropoff_lat, lng: draft.dropoff_lng }
        : await forwardGeocode(d);
      if (cancelled) return;
      if (!pg || !dg) {
        setQuote(null);
        setQuoteError("We couldn't pin one of the addresses precisely — our dispatcher will confirm the exact fare.");
        setQuoteLoading(false);
        return;
      }
      try {
        const blob = [draft.notes, draft.accessibility].filter(Boolean).join(" ");
        const hasDog = /\b(dog|pet|puppy|guide dog|assistance dog)\b/i.test(blob);
        const { data } = await api.post("/public/ai-booking/estimate", {
          pickup_lat: pg.lat, pickup_lng: pg.lng,
          dropoff_lat: dg.lat, dropoff_lng: dg.lng,
          vehicle_type: draft.vehicle_type || "car",
          passenger_count: draft.passenger_count || 1,
          has_dog: hasDog,
        });
        if (!cancelled) setQuote(data);
      } catch (e) {
        if (!cancelled) { setQuote(null); setQuoteError(formatApiError(e)); }
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [draft?.pickup_text, draft?.dropoff_text, draft?.pickup_lat, draft?.pickup_lng, draft?.dropoff_lat, draft?.dropoff_lng, draft?.vehicle_type, draft?.passenger_count, draft?.notes, draft?.accessibility]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { role: "customer", text: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/public/ai-booking/parse", { session_id: sessionIdRef.current, message: msg });
      setDraft(data.draft);
      setMeta({ missing_fields: data.missing_fields, ready_for_review: data.ready_for_review, confidence: data.confidence });
      const reply = data.follow_up_question || data.assistant_message || "Got it.";
      setMessages((m) => [...m, { role: "assistant", text: reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiError(e), error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const startOver = () => {
    const id = newSessionId();
    sessionIdRef.current = id;
    setSessionId(id);
    seededRef.current = false;
    setMessages([]); setDraft(null); setMeta(null); setInput("");
    setQuote(null); setQuoteError(null); setSubmitted(null); setDetailsOpen(false);
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/ai-booking/submit", { session_id: sessionIdRef.current });
      setSubmitted({ reference: data.reference, message: data.message });
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiError(e), error: true }]);
    } finally {
      setSubmitting(false);
    }
  };

  const isMissing = (f) => meta?.missing_fields?.includes(f);

  // Persist an inline edit (or an auto-fill) to the server draft — no LLM.
  // Updates the local draft + review-readiness so the confirm bar reacts.
  const applyPatch = async (fields) => {
    const { data } = await api.post("/public/ai-booking/patch", {
      session_id: sessionIdRef.current,
      fields,
    });
    setDraft(data.draft);
    setMeta((m) => ({ ...(m || {}), missing_fields: data.missing_fields, ready_for_review: data.ready_for_review }));
    return data;
  };

  const saveField = async (field, value) => {
    try {
      await applyPatch({ [field]: value });
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiError(e), error: true }]);
    }
  };

  // One booking state: seed the SERVER draft with the signed-in customer's known
  // account details (name/phone) BEFORE the assistant's first turn, so the AI
  // builds its questions from what we already know and never re-asks them. Silent
  // — the card first renders from the parse response. Also backfills if the user
  // signs in mid-conversation. Does NOT change the public endpoint's auth model.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!open || !user || seededRef.current) return;
    const fills = {};
    if (user.name) fills.customer_name = user.name;
    if (user.phone) fills.customer_phone = user.phone;
    if (!Object.keys(fills).length) return;
    seededRef.current = true;
    api.post("/public/ai-booking/patch", { session_id: sessionIdRef.current, fields: fills })
      .catch(() => { seededRef.current = false; });
  }, [open, user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Floating bubble — always visible on the public site */}
      {!open && (
        <button
          onClick={() => onOpenChange(true)}
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 h-14 pl-4 pr-5 rounded-full bg-[#002FA7] text-white shadow-xl hover:bg-[#001f6e] transition-colors"
          data-testid="web-ai-booking-bubble"
          aria-label="Book a taxi by chat"
        >
          <Wand2 className="w-5 h-5" />
          <span className="font-bold text-sm hidden sm:inline">Book by chat</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed inset-0 z-50 sm:inset-auto sm:bottom-5 sm:right-5 flex sm:block" data-testid="web-ai-booking-panel">
          <div className="sm:hidden absolute inset-0 bg-black/40" onClick={() => onOpenChange(false)} />
          <div className="relative w-full sm:w-[420px] max-h-screen sm:max-h-[80vh] m-auto sm:m-0 bg-white sm:rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between gap-2 px-4 py-3 bg-[#002FA7] text-white">
              <div className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center">
                  <Wand2 className="w-4 h-4" />
                </span>
                <div>
                  <div className="font-bold text-sm leading-tight">Book a taxi by chat</div>
                  <div className="text-[11px] text-white/70 leading-tight">Tell us your trip — we'll quote &amp; arrange it</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && !submitted && (
                  <button onClick={startOver} className="p-1.5 rounded-md hover:bg-white/15" title="Start over" data-testid="web-ai-booking-reset">
                    <RotateCcw className="w-4 h-4" />
                  </button>
                )}
                <button onClick={() => onOpenChange(false)} className="p-1.5 rounded-md hover:bg-white/15" title="Close" data-testid="web-ai-booking-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {submitted ? (
              /* Honest acknowledgement — request received, NOT booked */
              <div className="flex-1 overflow-y-auto p-6 text-center flex flex-col items-center justify-center gap-4" data-testid="web-ai-booking-submitted">
                <span className="w-16 h-16 rounded-full bg-[#DCFCE7] text-[#16A34A] flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8" />
                </span>
                <h3 className="font-heading text-xl font-black">Request received</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{submitted.message}</p>
                <div className="text-xs text-muted-foreground">
                  Your reference: <span className="font-mono-tab font-bold text-foreground" data-testid="web-ai-booking-reference">{submitted.reference}</span>
                </div>
                <Button onClick={startOver} variant="outline" className="mt-2" data-testid="web-ai-booking-new">
                  Make another request
                </Button>
              </div>
            ) : (
              <>
                {/* Messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFAFB]" data-testid="web-ai-booking-messages">
                  {messages.length === 0 && (
                    <div className="text-sm text-muted-foreground space-y-3">
                      <p className="flex items-center gap-1.5"><MessageSquare className="w-4 h-4" /> {welcome.intro}</p>
                      {welcome.examples.map((ex, i) => (
                        <button key={i} onClick={() => send(ex)}
                          className="block w-full text-left px-3 py-2 rounded-lg border border-border bg-white hover:border-[#002FA7] transition-colors text-foreground"
                          data-testid={`web-ai-booking-example-${i}`}>
                          “{ex}”
                        </button>
                      ))}
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "customer" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                          m.role === "customer"
                            ? "bg-[#002FA7] text-white rounded-br-sm"
                            : m.error ? "bg-[#FEF2F2] text-[#991B1B] border border-[#FECACA] rounded-bl-sm"
                            : "bg-white border border-border rounded-bl-sm"
                        }`}
                        data-testid={`web-ai-msg-${m.role}`}
                      >
                        {m.text}
                      </div>
                    </div>
                  ))}
                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-3.5 py-2 text-sm text-muted-foreground flex items-center gap-2">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
                      </div>
                    </div>
                  )}

                  {/* Draft + quote summary */}
                  {draft && (
                    <div className="border border-border rounded-xl bg-white p-3 space-y-2" data-testid="web-ai-booking-draft">
                      <EditableDraftRow icon={User} label="Name" value={draft.customer_name} missing={isMissing("customer_name")} field="customer_name" placeholder="Your name" onSave={saveField} testId="web-ai-draft-name" />
                      <EditableDraftRow icon={Phone} label="Phone" value={draft.customer_phone} missing={isMissing("customer_phone")} field="customer_phone" type="tel" placeholder="e.g. 07123 456789" onSave={saveField} testId="web-ai-draft-phone" />
                      <EditableDraftRow icon={MapPin} label="Pickup" value={draft.pickup_text} missing={isMissing("pickup_text")} field="pickup_text" placeholder="Pickup address or postcode" onSave={saveField} testId="web-ai-draft-pickup" />
                      <EditableDraftRow icon={MapPin} label="Destination" value={draft.dropoff_text} missing={isMissing("dropoff_text")} field="dropoff_text" placeholder="Destination address or postcode" onSave={saveField} testId="web-ai-draft-dropoff" />
                      <DraftRow icon={Clock} label="When" value={SCHEDULE_LABEL(draft.scheduled_for)} />
                      <div className="grid grid-cols-2 gap-2">
                        <DraftRow icon={Users} label="Passengers" value={String(draft.passenger_count || 1)} />
                        <DraftRow icon={CreditCard} label="Payment" value={draft.payment_method ? (draft.payment_method === "card" ? "Card" : "Cash") : null} missing={!draft.payment_method} />
                      </div>

                      {/* Payment selection — required before confirming. Tap Cash or Card. */}
                      <div data-testid="web-ai-payment">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">
                          Payment {!draft.payment_method && <span className="text-[#B45309] italic normal-case">· please choose</span>}
                        </div>
                        <div className="grid grid-cols-2 gap-px bg-border border border-border rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => saveField("payment_method", "cash")}
                            className={`py-2.5 flex items-center justify-center gap-1.5 text-xs font-bold transition-colors ${draft.payment_method === "cash" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-secondary"}`}
                            data-testid="web-ai-pay-cash"
                            aria-pressed={draft.payment_method === "cash"}
                          >
                            <Banknote className="w-3.5 h-3.5" /> CASH
                          </button>
                          <button
                            type="button"
                            onClick={() => saveField("payment_method", "card")}
                            className={`py-2.5 flex items-center justify-center gap-1.5 text-xs font-bold transition-colors ${draft.payment_method === "card" ? "bg-[#0A0A0A] text-white" : "bg-white hover:bg-secondary"}`}
                            data-testid="web-ai-pay-card"
                            aria-pressed={draft.payment_method === "card"}
                          >
                            <CreditCard className="w-3.5 h-3.5" /> CARD
                          </button>
                        </div>
                      </div>
                      {(draft.accessibility || draft.is_return) && (
                        <DraftRow icon={Accessibility} label="Notes" value={[draft.accessibility, draft.is_return ? "Return trip" : null].filter(Boolean).join(" · ")} />
                      )}

                      {/* Customer-friendly quote sheet */}
                      <div className="pt-2 mt-1 border-t border-dashed border-border" data-testid="web-ai-booking-quote">
                        {quoteLoading ? (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Estimating your fare…
                          </div>
                        ) : quote ? (
                          <>
                            <div className="grid grid-cols-3 gap-2 text-center my-1">
                              <div className="bg-secondary/50 rounded-lg py-2">
                                <div className="text-[10px] uppercase text-muted-foreground flex items-center justify-center gap-1"><Route className="w-3 h-3" /> Dist</div>
                                <div className="font-bold text-sm mt-0.5" data-testid="web-quote-distance">{quote.distance_mi} mi</div>
                              </div>
                              <div className="bg-secondary/50 rounded-lg py-2">
                                <div className="text-[10px] uppercase text-muted-foreground flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Time</div>
                                <div className="font-bold text-sm mt-0.5" data-testid="web-quote-duration">{Math.round(quote.duration_min)} min</div>
                              </div>
                              <div className="bg-[#002FA7] text-white rounded-lg py-2">
                                <div className="text-[10px] uppercase text-white/70">Est. fare</div>
                                <div className="font-heading font-black text-base mt-0.5" data-testid="web-quote-fare">£{Number(quote.fare).toFixed(2)}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1.5">
                              <Car className="w-3.5 h-3.5" /> {VEHICLE_LABEL[draft.vehicle_type] || "Standard Saloon"}
                              <span className="ml-auto text-[10px] italic">Estimate · final fare confirmed by dispatcher</span>
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-1" data-testid="web-quote-fare-label">
                              {quote.fare_label}
                            </div>
                            <button
                              type="button"
                              onClick={() => setDetailsOpen((v) => !v)}
                              className="mt-2 w-full flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline"
                              data-testid="web-quote-details-toggle"
                              aria-expanded={detailsOpen}
                            >
                              {detailsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                              Fare details
                            </button>
                            {detailsOpen && quote.breakdown && (
                              <div className="mt-1.5 border border-border rounded-lg p-2.5 space-y-1 bg-secondary/30 text-xs" data-testid="web-quote-details">
                                {fareLines(quote).map(([label, val], i) => (
                                  <div key={i} className="flex justify-between">
                                    <span className="text-muted-foreground">{label}</span>
                                    <span className="font-mono-tab">{val}</span>
                                  </div>
                                ))}
                                <div className="border-t border-border my-1" />
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Net (ex-VAT)</span>
                                  <span className="font-mono-tab">{gbp(quote.breakdown.subtotal_pre_vat_net)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">VAT{quote.breakdown.vat_rate ? ` (${Math.round(quote.breakdown.vat_rate * 100)}%)` : ""}</span>
                                  <span className="font-mono-tab">{gbp(quote.breakdown.vat_amount)}</span>
                                </div>
                                <div className="flex justify-between font-bold pt-0.5">
                                  <span>Total (inc VAT)</span>
                                  <span className="font-mono-tab">{gbp(quote.breakdown.gross ?? quote.fare)}</span>
                                </div>
                              </div>
                            )}
                          </>
                        ) : quoteError ? (
                          <div className="text-[11px] text-muted-foreground italic py-1">{quoteError}</div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm bar */}
                {meta?.ready_for_review && !!draft?.payment_method && (
                  <div className="border-t border-border px-3 py-2.5 bg-white">
                    <Button onClick={submit} disabled={submitting} className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="web-ai-booking-submit">
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Confirm &amp; send request
                    </Button>
                    <p className="text-[10px] text-muted-foreground text-center mt-1.5 flex items-center justify-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> A dispatcher reviews every request — your taxi isn't booked until we confirm.
                    </p>
                  </div>
                )}

                {/* Input */}
                <div className="border-t border-border p-2.5 flex items-center gap-2 bg-white">
                  <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder={welcome.placeholder}
                    className="flex-1 outline-none bg-secondary/40 rounded-full px-4 py-2 text-sm"
                    data-testid="web-ai-booking-input"
                    disabled={loading}
                  />
                  <Button onClick={() => send()} disabled={loading || !input.trim()} className="rounded-full bg-[#002FA7] hover:bg-[#001f6e] text-white h-9 w-9 p-0" data-testid="web-ai-booking-send">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default WebsiteAiBooking;
